import React, { useState } from "react";
import { supabase } from "../../supabase";
import { useNavigate } from "react-router-dom";
import "./BookingForm.css";

const BookingForm = ({ profile, onCancel }) => {
  const [bookingMessage, setBookingMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Get current date and time for minimum date validation
  const now = new Date();
  const currentDate = now.toISOString().split("T")[0];

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    if (!profile || !profile.user_id) {
      alert("Cannot identify the service provider.");
      return;
    }

    if (!bookingMessage.trim()) {
      alert("Please write a message to the service provider.");
      return;
    }

    if (!startDate || !startTime || !endDate || !endTime) {
      alert("Please select both start and end date/time for your booking.");
      return;
    }

    if (!location.trim()) {
      alert("Please specify the event location.");
      return;
    }

    // Create datetime objects for validation
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    // Validate dates
    if (startDateTime >= endDateTime) {
      alert("End date/time must be after start date/time.");
      return;
    }

    if (startDateTime < now) {
      alert("Start date/time cannot be in the past.");
      return;
    }

    // Calculate duration in hours
    const durationMs = endDateTime - startDateTime;
    const durationHours = durationMs / (1000 * 60 * 60);

    // Show loading state
    setIsSubmitting(true);

    try {
      const { data: sessionData, error: userError } =
        await supabase.auth.getSession();
      if (userError || !sessionData || !sessionData.session) {
        throw new Error("Error retrieving current user information.");
      }

      const senderId = sessionData.session.user.id;
      const receiverId = profile.user_id;

      if (!senderId || !receiverId) {
        throw new Error(
          "Unable to send booking request. User information is missing."
        );
      }

      // Get sender's information
      const { data: senderProfile, error: senderError } = await supabase
        .from("user_profiles")
        .select("full_name, email")
        .eq("user_id", senderId)
        .single();

      if (senderError) {
        throw new Error("Error retrieving your profile information.");
      }

      // Create a timestamp for both created_at and updated_at
      const timestamp = new Date().toISOString();

      // Insert booking request in booking_requests table
      const { data: bookingData, error: bookingError } = await supabase
        .from("booking_requests")
        .insert([
          {
            sender_id: senderId,
            receiver_id: receiverId,
            message: bookingMessage,
            location: location.trim(),
            status: "pending",
            start_datetime: startDateTime.toISOString(),
            end_datetime: endDateTime.toISOString(),
            duration_hours: durationHours,
            timezone: timezone,
            created_at: timestamp,
            updated_at: timestamp,
          },
        ]);

      if (bookingError) {
        throw new Error(
          "Error sending booking request to database: " + bookingError.message
        );
      }

      // Fetch receiver's email
      const { data: receiverProfile, error: emailError } = await supabase
        .from("user_profiles")
        .select("email")
        .eq("user_id", receiverId)
        .single();

      if (!emailError && receiverProfile && receiverProfile.email) {
        // Send email notification via Supabase Edge Functions
        await sendEmailNotification(
          receiverProfile.email,
          senderProfile.full_name,
          bookingMessage,
          profile.full_name,
          startDateTime,
          endDateTime,
          timezone,
          location
        );
      } else {
        console.error(
          "Could not send email notification:",
          emailError || "No receiver email found"
        );
      }

      alert(`Booking request sent to ${profile.full_name}`);
      onCancel(); // Close the form and reset state

      // Optionally redirect to the bookings page to see the sent request
      navigate("/bookings");
    } catch (error) {
      console.error("Booking request error:", error);
      alert(`Error: ${error.message || "Failed to send booking request"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setBookingMessage("");
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
    setLocation("");
    onCancel();
  };

  // Function to send email with message content and booking details
  const sendEmailNotification = async (
    email,
    senderName,
    message,
    receiverName,
    startDateTime,
    endDateTime,
    timezone,
    location
  ) => {
    try {
      const formatDateTime = (dateTime) => {
        return (
          dateTime.toLocaleDateString() + " at " + dateTime.toLocaleTimeString()
        );
      };

      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          to: email,
          subject: `New Booking Request from ${senderName}`,
          message: `${senderName} wants to book your services with the following details:

Event Details:
- Start: ${formatDateTime(startDateTime)}
- End: ${formatDateTime(endDateTime)}
- Duration: ${((endDateTime - startDateTime) / (1000 * 60 * 60)).toFixed(1)} hours
- Location: ${location}
- Timezone: ${timezone}

Message:
"${message}"

This booking request is also available in your Speaking Engagements page. Log in to respond.`,
        },
      });

      if (error) {
        console.error("Error sending email notification:", error);
      }
    } catch (err) {
      console.error("Exception sending email:", err);
    }
  };

  // Auto-set end date when start date changes
  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;
    setStartDate(selectedDate);

    // If end date is not set or is before start date, set it to start date
    if (!endDate || endDate < selectedDate) {
      setEndDate(selectedDate);
    }
  };

  // Auto-set end time when start time changes (add 1 hour default)
  const handleStartTimeChange = (e) => {
    const selectedTime = e.target.value;
    setStartTime(selectedTime);

    // If end time is not set, set it to 1 hour after start time
    if (!endTime) {
      const [hours, minutes] = selectedTime.split(":");
      const endHour = parseInt(hours) + 1;
      const endTimeString = `${endHour.toString().padStart(2, "0")}:${minutes}`;
      setEndTime(endTimeString);
    }
  };

  return (
    <div className="booking-form-container">
      <div className="booking-form-header">
        <h3>Send a Booking Request</h3>
        <button
          type="button"
          onClick={handleCancel}
          className="close-modal-button"
          aria-label="Close"
          disabled={isSubmitting}
        >
          ×
        </button>
      </div>
      <form onSubmit={handleBookingSubmit} className="booking-form">
        <div className="datetime-section">
          <h4>Event Details</h4>

          <div className="datetime-row">
            <div className="datetime-group">
              <label htmlFor="startDate">Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={handleStartDateChange}
                min={currentDate}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="datetime-group">
              <label htmlFor="startTime">Start Time:</label>
              <input
                type="time"
                id="startTime"
                value={startTime}
                onChange={handleStartTimeChange}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="datetime-row">
            <div className="datetime-group">
              <label htmlFor="endDate">End Date:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || currentDate}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="datetime-group">
              <label htmlFor="endTime">End Time:</label>
              <input
                type="time"
                id="endTime"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="location-group">
            <label htmlFor="location">Event Location:</label>
            <input
              type="text"
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., St. Mary's Church, London or Online via Zoom"
              required
              disabled={isSubmitting}
              className="location-input"
            />
            <small className="location-help-text">
              Enter the venue address, church name, or "Online" for virtual
              events
            </small>
          </div>

          <div className="timezone-group">
            <label htmlFor="timezone">Timezone:</label>
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={isSubmitting}
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="UTC">UTC</option>
              <option value={Intl.DateTimeFormat().resolvedOptions().timeZone}>
                {Intl.DateTimeFormat().resolvedOptions().timeZone} (Local)
              </option>
            </select>
          </div>
        </div>

        <div className="message-section">
          <label htmlFor="bookingMessage">Message:</label>
          <textarea
            id="bookingMessage"
            value={bookingMessage}
            onChange={(e) => setBookingMessage(e.target.value)}
            placeholder="Write your message here... Include any details about the service you're interested in."
            rows="5"
            className="booking-message"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="booking-form-buttons">
          <button
            type="submit"
            className="submit-booking"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Send Request"}
          </button>
          <button
            type="button"
            className="cancel-booking"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;
